// HtmlFormAttributes.jsx
import React from "react";

const HtmlFormAttributes = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Form Attributes - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML forms use various attributes to control how data is sent, displayed, and validated.
          Proper use of these attributes ensures functionality, accessibility, and user experience.
        </p>
      </section>

      {/* action Attribute */}
      <section id="action">
        <h2>2. Action Attribute</h2>
        <p>
          Specifies the URL to which the form data is sent when submitted.
        </p>
        <pre>
          <code>
{`<form action="/submit-form" method="post">
  <input type="text" name="username" />
  <input type="submit" value="Submit" />
</form>`}
          </code>
        </pre>
      </section>

      {/* method Attribute */}
      <section id="method">
        <h2>3. Method Attribute</h2>
        <p>
          Defines the HTTP method used when sending form data.
        </p>
        <ul>
          <li><strong>GET:</strong> Appends data to URL (not secure for sensitive info).</li>
          <li><strong>POST:</strong> Sends data in the request body (preferred for sensitive info).</li>
        </ul>
        <pre>
          <code>
{`<form action="/login" method="post">
  <input type="email" name="email" required />
  <input type="password" name="password" required />
  <input type="submit" value="Login" />
</form>`}
          </code>
        </pre>
      </section>

      {/* enctype Attribute */}
      <section id="enctype">
        <h2>4. Enctype Attribute</h2>
        <p>
          Specifies how form data should be encoded when sent to the server. Mostly used for file uploads.
        </p>
        <ul>
          <li><strong>application/x-www-form-urlencoded:</strong> Default encoding for simple forms.</li>
          <li><strong>multipart/form-data:</strong> Used for forms with file uploads.</li>
          <li><strong>text/plain:</strong> Sends data without encoding (rarely used).</li>
        </ul>
        <pre>
          <code>
{`<form action="/upload" method="post" enctype="multipart/form-data">
  <input type="file" name="fileUpload" />
  <input type="submit" value="Upload" />
</form>`}
          </code>
        </pre>
      </section>

      {/* autocomplete Attribute */}
      <section id="autocomplete">
        <h2>5. Autocomplete Attribute</h2>
        <p>
          Enables or disables browser autofill for form inputs.
        </p>
        <ul>
          <li><strong>on:</strong> Browser will suggest previously entered values.</li>
          <li><strong>off:</strong> Browser will not suggest values.</li>
        </ul>
        <pre>
          <code>
{`<form action="/signup" method="post" autocomplete="off">
  <input type="text" name="username" />
  <input type="password" name="password" />
  <input type="submit" value="Sign Up" />
</form>`}
          </code>
        </pre>
      </section>

      {/* target Attribute */}
      <section id="target">
        <h2>6. Target Attribute</h2>
        <p>
          Defines where to display the response after submitting the form.
        </p>
        <ul>
          <li><strong>_self:</strong> Default, loads in the same frame.</li>
          <li><strong>_blank:</strong> Loads in a new tab or window.</li>
          <li><strong>_parent:</strong> Loads in the parent frame.</li>
          <li><strong>_top:</strong> Loads in the full body of the window.</li>
        </ul>
        <pre>
          <code>
{`<form action="/submit" method="post" target="_blank">
  <input type="text" name="query" />
  <input type="submit" value="Search" />
</form>`}
          </code>
        </pre>
      </section>

      {/* novalidate Attribute */}
      <section id="novalidate">
        <h2>7. Novalidate Attribute</h2>
        <p>
          Disables browser-based validation for the form.
        </p>
        <pre>
          <code>
{`<form action="/submit" method="post" novalidate>
  <input type="email" name="email" required />
  <input type="submit" value="Submit" />
</form>`}
          </code>
        </pre>
      </section>

      {/* accept-charset Attribute */}
      <section id="accept-charset">
        <h2>8. Accept-Charset Attribute</h2>
        <p>
          Specifies the character encodings for the form submission.
        </p>
        <pre>
          <code>
{`<form action="/submit" method="post" accept-charset="UTF-8">
  <input type="text" name="username" />
  <input type="submit" value="Submit" />
</form>`}
          </code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>9. Best Practices</h2>
        <ul>
          <li>Always use <strong>method="post"</strong> for sensitive data.</li>
          <li>Use <strong>enctype="multipart/form-data"</strong> for file uploads.</li>
          <li>Use <strong>autocomplete</strong> wisely to enhance user experience.</li>
          <li>Ensure proper <strong>action</strong> URLs are specified.</li>
          <li>Use <strong>target</strong> only when necessary for new tab/window behavior.</li>
          <li>Validate your forms, but disable with <strong>novalidate</strong> only if using custom validation.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlFormAttributes;
